import { Component, OnInit, Input, Output, EventEmitter, ChangeDetectionStrategy } from "@angular/core";
import { FormBuilder, FormGroup, MaxLengthValidator, Validators } from "@angular/forms";
import { ChangeRequestAddressModel } from "src/app/models/change-requests";
import { PayrollAddressModel } from "src/app/models/payroll";
import { FormValidationService } from "src/app/services/form-validation.service";
import { USStates } from "src/app/constants/us-states";
import { PayrollService } from "src/app/services/payroll.service";
import { MaskType } from "src/app/constants/masks";
import { ChangeRequestStatus } from "src/app/constants/change-request";

@Component({
    selector: 'mwp-payroll-address-form',
    templateUrl: './address-form.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PayrollAddressFormComponent implements OnInit {
    @Input() public title: string;
    @Input() public payrollAddressModel: PayrollAddressModel; 
    @Input() public ChangeRequestAddressModel: ChangeRequestAddressModel;
    @Input() public isEditing: boolean;
    @Output() public cancelEmit: EventEmitter<ChangeRequestAddressModel> = new EventEmitter<ChangeRequestAddressModel>();

    public addressModel: ChangeRequestAddressModel;
    public addressForm: FormGroup;
    public verifyAddressModel: ChangeRequestAddressModel;
    public usStates = USStates;
    public errorMessage: string;
    public maskType = MaskType;
    public verifyTitle: string = 'Please verify and review the address';
    public isDenied: boolean;
    public deniedReason: string;
    public lastUpdatedOn: string;

    constructor(
        private formBuilder: FormBuilder,
        private payrollService: PayrollService,
    ) {
    }

    ngOnInit(): void {
        if (this.ChangeRequestAddressModel) {
            this.addressForm = this.formBuilder.group({
                addressOne: [{ value: this.ChangeRequestAddressModel.address1, disabled: !this.isEditing }, Validators.required],
                addressTwo: [{ value: this.ChangeRequestAddressModel.address2, disabled: !this.isEditing }],
                city: [{ value: this.ChangeRequestAddressModel.city, disabled: !this.isEditing }, Validators.required],
                homeState: [{ value: this.ChangeRequestAddressModel.state, disabled: !this.isEditing }, Validators.required],
                zipCode: [{ value: this.ChangeRequestAddressModel.zipCode, disabled: !this.isEditing }, [Validators.required, Validators.compose([FormValidationService.validZipcode(true)])]],
            });

            if(this.ChangeRequestAddressModel.changeRequestStatusID == ChangeRequestStatus.denied) {
                this.isDenied = true;
                this.deniedReason = this.ChangeRequestAddressModel.changeRequestDeniedReason;
            }
        } else {
            this.addressForm = this.formBuilder.group({
                addressOne: [{ value: this.payrollAddressModel.homeAddressLine1, disabled: !this.isEditing }, Validators.required],
                addressTwo: [{ value: this.payrollAddressModel.homeAddressLine2, disabled: !this.isEditing }],
                city: [{ value: this.payrollAddressModel.homeCity, disabled: !this.isEditing }, Validators.required],
                homeState: [{ value: this.payrollAddressModel.homeState, disabled: !this.isEditing }, Validators.required],
                zipCode: [{ value: this.payrollAddressModel.homeZipCode, disabled: !this.isEditing }, [Validators.required, Validators.compose([FormValidationService.validZipcode(true)])]],
            });

            if (this.payrollAddressModel.lastUpdated)
                this.lastUpdatedOn = this.payrollAddressModel.lastUpdated;
        }

        this.addressForm.get('addressOne').updateValueAndValidity();
    }

    private rebuildForm(disableForm: boolean): void {
        this.addressForm = this.formBuilder.group({
            addressOne: [{ value: this.addressModel.address1, disabled: disableForm }, Validators.required],
            addressTwo: [{ value: this.addressModel.address2, disabled: disableForm }],
            city: [{ value: this.addressModel.city, disabled: disableForm }, Validators.required],
            homeState: [{ value: this.addressModel.state, disabled: disableForm }, Validators.required],
            zipCode: [{ value: this.addressModel.zipCode, disabled: disableForm }, [Validators.required, Validators.compose([FormValidationService.validZipcode(true)])]],
        });
    }

    public verify(): void {
        this.addressModel = new ChangeRequestAddressModel();
        this.addressModel.id = this.ChangeRequestAddressModel.id;
        this.addressModel.address1 = this.addressForm.get('addressOne').value;
        this.addressModel.address2 = this.addressForm.get('addressTwo').value;
        this.addressModel.city = this.addressForm.get('city').value;
        this.addressModel.state = this.addressForm.get('homeState').value;
        this.addressModel.zipCode = this.addressForm.get('zipCode').value;

        this.payrollService.verifyChangeAdress(this.addressModel).subscribe((response: ChangeRequestAddressModel) => {
            if (response.errorMessage) {
                this.errorMessage = response.errorMessage;
            } else {
                this.errorMessage = null;
                this.verifyAddressModel = response;
                this.rebuildForm(true);
            }
        });
    }

    public resetVerify(): void {
        this.verifyAddressModel = null;
        this.rebuildForm(!this.isEditing);
    }

    public save(): void {
        const l_payload = new ChangeRequestAddressModel();
        l_payload.id = this.ChangeRequestAddressModel.id;
        l_payload.address1 = this.addressForm.get('addressOne').value;
        l_payload.address2 = this.addressForm.get('addressTwo').value;
        l_payload.city = this.addressForm.get('city').value;
        l_payload.state = this.addressForm.get('homeState').value;
        l_payload.zipCode = this.addressForm.get('zipCode').value;

        this.payrollService.saveAdressChange(l_payload).subscribe((response: ChangeRequestAddressModel) => {
            if (response.errorMessage) {
                this.errorMessage = response.errorMessage;
            } else {
                this.cancelEmit.emit(response);        
            }
        });
    }

    public cancel() {
        this.cancelEmit.emit();
    }
}